import { Locale } from './locale';
import { FooterLinksInterface, euFooterLinks, naFooterLinks, laFooterLinks } from 'utils/links';
const { location } = window;

const {
  'en-US': enUS, 'en-CA': enCA, 'en-GB': enGB, 'en-IE': enIE, 'en-AR': enAR,'en-NL': enNL,
  'es-AR': esAR,
  'de-AT': deAT, 'de-DE': deDE,
  'fr-CA': frCA,
  'nl-NL': nlNL,
} = Locale;

export class Region {
  // constructor needs to come first here so that the instance fields it declares/instantiates can be referenced in static properties below
  private constructor(
    public readonly code: RegionCode,
    public readonly domains: RegionDomainMap,

    public readonly footerLinks: FooterLinksInterface,

    public readonly locales: Locale[],
    public readonly defaultLocale = locales[0],

    public readonly countries = locales.map(locale => locale.country),
    public readonly languages = locales.map(locale => locale.language)
  ) {}

  public static readonly NA = new Region(
    'NA',
    {
      prod: 'https://www.clover.com',
      stg: 'https://stg1.dev.clover.com'
    },
    naFooterLinks,
    [enUS, enCA, frCA],
  );

  public static readonly LA = new Region(
    'LA',
    {
      prod: 'https://www.la.clover.com',
      stg: 'https://stg2.dev.clover.com',
      dev: 'https://dev7.dev.clover.com'
    },
    laFooterLinks,
    [esAR, enAR],
  );

  public static readonly EU = new Region(
    'EU',
    {
      prod: 'https://www.eu.clover.com',
      stg: 'https://stg3.dev.clover.com',
      dev: 'https://demo2.dev.clover.com'
    },
    euFooterLinks,
    [enGB, enIE, enNL, deDE, deAT, nlNL],
  );

  // Exclude QA region
  public static readonly ALL = [
    Region.NA,
    Region.EU,
    Region.LA,
  ];

  public static readonly PREPROD_ENV_REGEX = {
    sandbox: /^(?:sandbox|sandboxdev)\.dev/,
    stg: /^(?:stg)\d+\.dev/,
    dev: /^(?:dev|demo)\d+\.dev/,
    local: /^localhost/,
    preview: /^.*\.netlify\.app/,
  };

  public static readonly QA_ENV_REGEX = /qa\d{1,}\.dev\.clover\.com/;
  public static readonly CURRENT_DOMAIN = location.origin;

  public static readonly QA = new Region('QA', {}, naFooterLinks, [
    enUS,
    enCA,
    enGB,
    enIE,
    esAR,
    deDE,
    deAT,
    frCA,
    nlNL,
  ]);

  public static DEFAULT_LANGUAGE: DeepReadonly<{
    [K in RegionCode]: LanguageTag[]
  }> = {
    QA: ['en'],
    EU: ['en-GB', 'en'],
    LA: ['es-AR', 'en'],
    NA: ['en'],
  };

  private static get isLocalhostOrQA(): boolean {
    const { host } = location;

    const isLocal = sessionStorage.getItem('isLocalOverride')
      ? sessionStorage.getItem('isLocalOverride') === 'true'
      : host.includes('localhost');

    const isQA = sessionStorage.getItem('isQaOverride')
      ? sessionStorage.getItem('isQaOverride') === 'true'
      : Region.QA_ENV_REGEX.test(host);

    return isLocal || isQA;
  }

  public static get current(): Region {
    const regionOverride = sessionStorage.getItem('regionOverride'); // na | eu | la | qa

    if (regionOverride) {
      return {
        qa: Region.QA,
        eu: Region.EU,
        la: Region.LA
      }[regionOverride.toLowerCase()] || Region.NA;
    }

    const regionToReturn = Region.ALL.find(region => Object.values(region.domains).includes(Region.CURRENT_DOMAIN)) || Region.NA;

    return this.isLocalhostOrQA ? Region.QA : regionToReturn;
  }

  public static readonly SUPPORTED_LOCALES = Region.current.locales;

  public static getLocales(isLoggedIn = false) : Locale[] {
    const supportedLocalesOnly = isLoggedIn && Region.current !== Region.QA;

    return supportedLocalesOnly ? Region.SUPPORTED_LOCALES.filter(l => l.enabled) : Locale.ALL;
  }

  /**
   * Given a locale returns the default data-center region.
   * Localhost will default to QA region.
   * Example: en-CA locale => NA region
   */
  public static getDefaultRegion(locale: Locale): Region {
    const NonQARegionWithFallback = Region.ALL.find(region => !!region.locales.find(l => l && l.languageTag === locale.languageTag)) || Region.NA;

    return this.isLocalhostOrQA ? Region.QA : NonQARegionWithFallback;
  }

  /**
   * Given a locale returns the current pathname in the correct data-center for the locale.
   * Also adds query param to ensure that i18n uses the correct locale after redirecting to the Href.
   */
  public static getRedirectUrl(locale: Locale): string {
    const { location : localLocation } = window;
    const environments = Object.keys(Region.PREPROD_ENV_REGEX);
    const targetEnv = environments.find(env => Region.PREPROD_ENV_REGEX[env as keyof typeof Region.PREPROD_ENV_REGEX].test(localLocation.hostname)) || 'prod';
    const regionDomains = Region.getDefaultRegion(locale).domains;
    const origin = regionDomains[targetEnv as keyof typeof regionDomains] || localLocation.origin;

    return `${origin}${localLocation.pathname}?lng=${locale.languageTag}`;
  }
}
